import { useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import type { UnifiedRemediationItem } from '../../services/unifiedRemediationApi'
import styles from '../../styles/pages/compliance-center/UnifiedRemediationRegistry.module.scss'

const PREFIX = 'complianceCenterUi.remediationAuthority'

type Props = {
  items: UnifiedRemediationItem[]
  loading?: boolean
  crossStandardImpactByControl?: Record<
    string,
    { totalReadinessGain?: number; impacts?: Array<{ standardCode?: string; readinessGain?: number }> }
  >
}

function severityRowClass(severity: string): string {
  const s = String(severity || '').toUpperCase()
  if (s === 'CRITICAL') return styles.rowCritical
  if (s === 'HIGH') return styles.rowHigh
  if (s === 'MEDIUM') return styles.rowMedium
  return styles.rowLow
}

function severityBadgeClass(severity: string): string {
  const s = String(severity || '').toUpperCase()
  if (s === 'CRITICAL') return styles.severityCritical
  if (s === 'HIGH') return styles.severityHigh
  if (s === 'MEDIUM') return styles.severityMedium
  return styles.severityLow
}

function formatDate(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toISOString().slice(0, 10)
}

function primarySourceLabel(item: UnifiedRemediationItem): string {
  const first = item.sources?.[0]
  return first?.label || first?.type || '—'
}

function allSourceLabels(item: UnifiedRemediationItem): string {
  const labels = (item.sources || [])
    .map((s) => s.label || s.type)
    .filter(Boolean)
  return labels.length ? labels.join(' · ') : '—'
}

function DetailSection({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <div>
      <p className={styles.detailSectionTitle}>{title}</p>
      {children}
    </div>
  )
}

function RemediationRow({
  item,
  crossStandardImpactByControl,
}: {
  item: UnifiedRemediationItem
  crossStandardImpactByControl?: Props['crossStandardImpactByControl']
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)

  const statusClass =
    item.status === 'BLOCKED'
      ? styles.statusBlocked
      : item.isOverdue
        ? styles.statusOverdue
        : styles.statusChip

  const controlKey = item.controlKey || item.linkedControls?.[0]?.controlCode
  const policyId = item.linkedPolicies?.[0]?.id
  const walkthroughQuery = controlKey
    ? `?control=${encodeURIComponent(controlKey)}`
    : ''

  return (
    <article className={`${styles.row} ${severityRowClass(item.severity)}`}>
      <button
        type="button"
        className={styles.rowHeader}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className={`${styles.severityBadge} ${severityBadgeClass(item.severity)}`}>
          {item.severity}
        </span>
        <div className={styles.rowTitleBlock}>
          <p className={styles.rowTitle}>{item.title}</p>
          <div className={styles.rowMetaGrid}>
            <p className={styles.metaItem}>
              <span className={styles.metaLabel}>{t(`${PREFIX}.source`)}: </span>
              {primarySourceLabel(item)}
            </p>
            <p className={styles.metaItem}>
              <span className={styles.metaLabel}>{t(`${PREFIX}.remediationStatus`)}: </span>
              <span className={statusClass}>{item.status}</span>
            </p>
            <p className={styles.metaItem}>
              <span className={styles.metaLabel}>{t(`${PREFIX}.due`)}: </span>
              {formatDate(item.dueDate)}
            </p>
          </div>
        </div>
        <span className={styles.expandIcon} aria-hidden>
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {expanded ? (
        <div className={styles.rowBody}>
          <div className={styles.detailGrid}>
            {item.recommendations?.length ? (
              <DetailSection title={t(`${PREFIX}.recommendation`)}>
                <ul className={styles.detailList}>
                  {item.recommendations.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </DetailSection>
            ) : null}

            {controlKey && crossStandardImpactByControl?.[controlKey] ? (
              <DetailSection title={t(`${PREFIX}.crossFrameworkBenefit`, 'Expected Cross-Framework Benefit')}>
                <ul className={styles.detailList}>
                  {(crossStandardImpactByControl[controlKey].impacts || []).map((row) => (
                    <li key={`${controlKey}-${row.standardCode}`}>
                      {row.standardCode}: +{row.readinessGain ?? 0}%
                    </li>
                  ))}
                  <li>
                    {t(`${PREFIX}.crossFrameworkTotal`, 'Total')}: +
                    {crossStandardImpactByControl[controlKey].totalReadinessGain ?? 0}%
                  </li>
                </ul>
              </DetailSection>
            ) : null}

            {item.requiredEvidence?.length ? (
              <DetailSection title={t(`${PREFIX}.requiredEvidence`)}>
                <ul className={styles.detailList}>
                  {item.requiredEvidence.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </DetailSection>
            ) : null}

            {item.linkedControls?.length ? (
              <DetailSection title={t(`${PREFIX}.affectedControls`)}>
                <div className={styles.chipRow}>
                  {item.linkedControls.map((c) => (
                    <span key={`${c.standardCode}-${c.controlCode}`} className={styles.linkChip}>
                      {c.controlTitle
                        ? `${c.controlCode} — ${c.controlTitle}`
                        : (c.controlCode || '—')}
                    </span>
                  ))}
                </div>
              </DetailSection>
            ) : null}

            {item.linkedSystems?.length ? (
              <DetailSection title={t(`${PREFIX}.affectedSystems`)}>
                <div className={styles.chipRow}>
                  {item.linkedSystems.map((s) => (
                    <span key={s.informationSystemId || s.id} className={styles.linkChip}>
                      {s.name || s.informationSystemId || s.id}
                    </span>
                  ))}
                </div>
              </DetailSection>
            ) : null}

            {item.linkedFindings?.length ? (
              <DetailSection title={t(`${PREFIX}.openFindings`)}>
                <ul className={styles.detailList}>
                  {item.linkedFindings.map((f) => (
                    <li key={f.id}>{f.title || f.id}</li>
                  ))}
                </ul>
              </DetailSection>
            ) : null}

            {item.linkedPolicies?.length ? (
              <DetailSection title={t(`${PREFIX}.linkedPolicies`)}>
                <ul className={styles.detailList}>
                  {item.linkedPolicies.map((p) => (
                    <li key={p.id}>{p.title || p.code || p.id}</li>
                  ))}
                </ul>
              </DetailSection>
            ) : null}

            {item.linkedEvidence?.length ? (
              <DetailSection title={t(`${PREFIX}.linkedEvidence`)}>
                <ul className={styles.detailList}>
                  {item.linkedEvidence.map((e) => (
                    <li key={e.id}>{e.title || e.id}</li>
                  ))}
                </ul>
              </DetailSection>
            ) : null}

            <DetailSection title={t(`${PREFIX}.sourcesLabel`)}>
              <p className={styles.metaItem}>{allSourceLabels(item)}</p>
              {item.owner ? (
                <p className={styles.metaItem}>
                  <span className={styles.metaLabel}>{t(`${PREFIX}.owner`)}: </span>
                  {item.owner}
                </p>
              ) : null}
              {item.blockedReason ? (
                <p className={styles.metaItem}>
                  <span className={styles.metaLabel}>{t(`${PREFIX}.blockers`)}: </span>
                  {item.blockedReason}
                </p>
              ) : null}
            </DetailSection>
          </div>

          <div className={styles.shortcuts}>
            <Link className={styles.linkChip} to="/compliance-center/evidence">
              {t(`${PREFIX}.openEvidenceHub`)}
            </Link>
            <Link className={styles.linkChip} to="/compliance-center/evidence?upload=1">
              {t(`${PREFIX}.uploadEvidence`)}
            </Link>
            {policyId ? (
              <Link
                className={styles.linkChip}
                to={`/compliance-center/policies?policyId=${encodeURIComponent(policyId)}`}
              >
                {t(`${PREFIX}.openPolicy`)}
              </Link>
            ) : null}
            {controlKey ? (
              <button
                type="button"
                className={styles.linkChip}
                onClick={() =>
                  navigate(`/compliance-center/operational-workflow${walkthroughQuery}`)
                }
              >
                {t(`${PREFIX}.openWalkthrough`)}
              </button>
            ) : null}
          </div>

          <p className={styles.verifiedNote}>{t(`${PREFIX}.verifiedNote`)}</p>
        </div>
      ) : null}
    </article>
  )
}

export function UnifiedRemediationRegistry({ items, loading, crossStandardImpactByControl }: Props) {
  const { t } = useI18n()

  const sorted = useMemo(
    () =>
      [...items].sort(
        (a, b) => (b.priorityScore || 0) - (a.priorityScore || 0),
      ),
    [items],
  )

  if (loading) {
    return <p className={styles.emptyState}>{t('common.loading')}</p>
  }

  if (!sorted.length) {
    return <p className={styles.emptyState}>{t(`${PREFIX}.noItems`)}</p>
  }

  return (
    <div className={styles.registryStack}>
      {sorted.map((item) => (
        <RemediationRow key={item.id} item={item} crossStandardImpactByControl={crossStandardImpactByControl} />
      ))}
    </div>
  )
}
